import { ContextMenu, ContextMenuItem, ContextMenuModel } from "./custom-context-menu";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";
import { TimesheetShortcutsSet } from "./timesheet-shortcuts-set";
import { Txt } from "../common/text";
import { C } from "../common/component";
import { ModalBuilder } from "../common/modal-builder";
import { TimesheetSelectedCells } from "./timesheet-right-table-configurer";
import { TimesheetCellButton, TimesheetShortcut } from "./timesheet-models";
import { SheetClip } from "../external/sheetclip";

export class HotContextMenuAdapter
{
    private handler?: (e: MouseEvent) => void;
    private menu?: ContextMenu;
    private readonly targets: HTMLElement[] = [];
    private clipboardGrid: string[][] | null = null;

    constructor(private readonly commentsManager: TimesheetCommentsManager,
                private readonly dataManager: TimesheetRightTableDataManager,
                private readonly shortcuts: TimesheetShortcutsSet,
                private readonly multiCellButtons: TimesheetCellButton[])
    {
    }

    attach(el: HTMLElement, hot: Handsontable): void
    {
        if (!this.handler)
        {
            this.handler = (e) => this.onContextMenu(e, hot);
        }
        el.addEventListener("contextmenu", this.handler, true);
        this.targets.push(el);
    }

    attachToHot(hot: Handsontable): void
    {
        const anyHot = hot as any;
        const container: HTMLElement = anyHot.container ?? anyHot.rootElement;

        const selectors = [
            container,
            ".ht_master",
            ".ht_clone_top",
            ".ht_clone_left",
            ".ht_clone_corner"
        ];

        selectors.forEach((selector) =>
        {
            const element = typeof selector === "string"
                ? container.querySelector<HTMLElement>(selector)
                : selector;

            if (element)
            {
                this.attach(element, hot);
            }
        });
    }

    destroy(): void
    {
        if (this.handler)
        {
            this.targets.forEach((target) => target.removeEventListener("contextmenu", this.handler, true));
        }
        this.menu?.destroy();
        this.targets.length = 0;
        this.clipboardGrid = null;
    }

    private onContextMenu(e: MouseEvent, hot: Handsontable): void
    {
        e.preventDefault();
        e.stopPropagation();

        const td = (e.target as HTMLElement).closest("td");
        if (!td)
        {
            return;
        }
        const coords = this.getCellCoords(td, hot);
        if (!coords)
        {
            return;
        }

        const selectedCells = this.getSelectedCells(hot);
        if (selectedCells == null || coords.row < selectedCells.startRow || coords.row > selectedCells.endRow ||
            coords.col < selectedCells.startColumn || coords.col > selectedCells.endColumn )
        {
            return;
        }

        this.menu?.destroy();
        this.menu = new ContextMenu(this.buildModel(selectedCells, hot));
        this.menu.open(e);
    }

    private getSelectedCells(hot: Handsontable): TimesheetSelectedCells
    {
        const coords = hot.getSelectedLast();
        if (!coords || coords.length < 4)
        {
            console.error("Selection error: ", coords)
            return null;
        }

        const selectedCells = new TimesheetSelectedCells();
        selectedCells.startRow = Math.min(coords[0], coords[2]);
        selectedCells.endRow = Math.max(coords[0], coords[2]);
        selectedCells.startColumn = Math.min(coords[1], coords[3]);
        selectedCells.endColumn = Math.max(coords[1], coords[3]);
        return selectedCells;
    }

    private getCellCoords(td: HTMLElement, hot: Handsontable): any
    {
        const coords = hot.getCoords(td) as any;
        if (!coords || coords.row < 0 || coords.col < 0)
        {
            return null;
        }
        return coords;
    }

    private buildModel(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuModel
    {
        return {
            items: [
                this.createCopyItem(selectedCells, hot),
                this.createPasteItem(selectedCells, hot),
                ...this.createEditCommentItem(selectedCells, hot),
                ...this.createChangeRowCommentItem(selectedCells, hot),
                ...this.createCustomButtons(selectedCells),
                // ...this.createShortcutsItems(selectedCells, hot),
            ]
        };
    }

    private createCustomButtons(selectedCells: TimesheetSelectedCells): ContextMenuItem[]
    {
        return this.createCustomButtonsCore(selectedCells.isSingleCell()
            ? this.dataManager.getButtons(selectedCells.startRow, selectedCells.startColumn)
            : this.multiCellButtons, selectedCells);
    }

    private createCustomButtonsCore(buttons: TimesheetCellButton[], selectedCells: TimesheetSelectedCells): ContextMenuItem[]
    {
        const result: ContextMenuItem[] = [];
        for (const button of buttons)
        {
            result.push({
                text: () => button.Name,
                disabled: () => button.IsDisabled,
                onClick: button.Url ? () => this.dataManager.doButtonAction(button, selectedCells) : null,
                submenu: button.SubButtons.length > 0 ? this.createCustomButtonsCore(button.SubButtons, selectedCells) : null
            });
        }
        return result;
    }

    private createCopyItem(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem
    {
        return {
            text: () => Txt.copy,
            onClick: async () =>
            {
                const grid = this.copySelectionAsGrid(selectedCells, hot);
                if (!grid)
                {
                    return;
                }
                this.clipboardGrid = grid;
                try
                {
                    const text = SheetClip.stringify(grid);
                    await navigator.clipboard.writeText(text);
                }
                catch (error)
                {
                    // Silently fail if clipboard access is denied
                    console.debug("Clipboard write failed:", error);
                }
            }
        };
    }

    private createPasteItem(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem
    {
        return {
            text: () => Txt.paste,
            onClick: async () =>
            {
                try
                {
                    const grid = await navigator.clipboard.readText();
                    this.clipboardGrid = SheetClip.parse(grid);
                }
                catch (error)
                {
                    // Silently fail if clipboard access is denied
                    console.debug("Clipboard read failed:", error);
                }
                if (!this.clipboardGrid)
                {
                    return;
                }

                const changes: [number, number, string][] = [];
                this.clipboardGrid.forEach((rowData, rowIndex) =>
                {
                    rowData.forEach((value, colIndex) =>
                    {
                        const r = selectedCells.startRow + rowIndex;
                        const c = selectedCells.startColumn + colIndex;
                        changes.push([r, c, value]);
                    });
                });

                if (changes.length > 0)
                {
                    hot.setDataAtCell(changes);
                }
            }
        };
    }

    private createEditCommentItem(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem[]
    {
        if (!selectedCells.isSingleCell())
        {
            return [];
        }
        return [{
            text: () => Txt.addComment,
            disabled: () => !this.commentsManager.isCommentEditable(selectedCells.startRow),
            onClick: () =>
            {
                const cell = hot.getCell(selectedCells.startRow, selectedCells.startColumn);
                const cellElement = C.create(<HTMLElement>cell);
                this.commentsManager.openNewComment(cellElement, selectedCells.startRow, selectedCells.startColumn);
            }
        }];
    }

    private createChangeRowCommentItem(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem[]
    {
        if (selectedCells.startRow !== selectedCells.endRow)
        {
            return [];
        }
        return [{
            text: () => Txt.changeRowComment,
            disabled: () => !this.commentsManager.isCommentEditable(selectedCells.startRow),
            onClick: () =>
            {
                const row = selectedCells.startRow;

                const modal = ModalBuilder.createModal(Txt.comment);
                modal.body.addClass("change-password");

                const currentComment = this.commentsManager.getComment(row, null);

                const commentInput = C.input
                    .addClass("form-control")
                    .attr("type", "text")
                    .attr("placeholder", Txt.comment)
                    .val(currentComment)
                    .appendTo(modal.body);

                const saveButton = C.button
                    .addClass("btn btn-primary main-button")
                    .text(Txt.save)
                    .appendTo(modal.body);

                saveButton.click(async () =>
                {
                    const commentValue = String(commentInput.val());
                    await this.commentsManager.saveComment(row, null, commentValue);
                    const rowHeaderFn = (rowNumber: number): string =>
                        this.dataManager.getRowHeader(rowNumber, this.commentsManager);
                    hot.updateSettings({ rowHeaders: rowHeaderFn as unknown as any }, false);
                    ModalBuilder.removeModal(modal.container);
                });

                modal.container.modal({
                    backdrop: "static",
                    keyboard: true,
                    show: true
                });
            }
        }];
    }

    private createShortcutsItems(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem[]
    {
        if (!selectedCells.isSingleCell())
        {
            return [];
        }
        return this.shortcuts.getDisplayGroups().map((group) => ({
            text: () => group.displayName,
            submenu: group.displayedShortcuts.map((shortcut: TimesheetShortcut) => ({
                text: () => shortcut.Abbreviation
                    ? `${shortcut.Name} (${shortcut.Abbreviation})`
                    : shortcut.Name,
                onClick: () =>
                {
                    if (shortcut.Abbreviation)
                    {
                        hot.setDataAtCell(selectedCells.startRow, selectedCells.startColumn, shortcut.Abbreviation);
                    }
                }
            }))
        }));
    }

    private copySelectionAsGrid(selectedCells: TimesheetSelectedCells, hot: Handsontable): string[][]
    {
        const grid: string[][] = [];
        for (let r = selectedCells.startRow; r <= selectedCells.endRow; r++)
        {
            const row: string[] = [];
            for (let c = selectedCells.startColumn; c <= selectedCells.endColumn; c++)
            {
                const v = hot.getDataAtCell(r, c);
                row.push(v == null ? "" : String(v));
            }
            grid.push(row);
        }
        return grid;
    }
}
